import Link from 'next/link';
import { useState, useEffect } from 'react';
import { Locale } from '@peloton/internationalize/models/locale';
import useBasicsPackagesMap from '@ecomm/commercetools/hooks/useBasicsPackagesMap';

const TestRoutes = {
  ctPricesHandler: '/api/ctPricesHandler/[locale]',
  // freeformPageHander: '/api/freeformPageHander/[locale]/SLUG', TODO: This is not an active route right now
  mwiInstructorListHandler: '/api/mwiInstructorListHandler/[locale]',
  instructorsHandler: '/api/instructorsHandler/[locale]',
  pageBuilderAllPromosCfuHandler: '/api/pageBuilderAllPromosCfuHandler/[locale]/bike',
  pageBuilderAllPromosHandler: '/api/pageBuilderAllPromosHandler/[locale]',
  pageBuilderEntryHandler: '/api/pageBuilderEntryHandler/[locale]/9qYMtbKUnlPoyBdZXi7QJ',
  pageBuilderGroupedPromosHandler: '/api/pageBuilderGroupedPromosHandler/[locale]',
  pageBuilderPageSlugsHandler: '/api/pageBuilderPageSlugsHandler/5OlkgOV9X3TwozIdA4qJs4',
  pageBuilderPromoCfuHandler: '/api/pageBuilderPromoCfuHandler/[locale]/bike',
  pageBuilderPromoCfuSlugHandler:
    '/api/pageBuilderPromoCfuSlugHandler/[locale]/bike/bundles/[bundleSlug]',
  pageBuilderPromoHandler: '/api/pageBuilderPromoHandler/[locale]',
  pageBuilderQueryHandler:
    '/api/pageBuilderQueryHandler/[locale]?content_type=componentGenericTextWithMedia&fields.name=Bike%2B+Power+Adapter+and+Cable',
  peloContentEntryHandler: '/api/peloContentEntryHandler/[locale]/7HIi2PeCwFsyW1gDnqtdop',
};

const locales: Locale[] = [
  Locale.Default,
  Locale.EnglishCanada,
  Locale.EnglishUnitedKingdom,
  Locale.EnglishAustralia,
  Locale.GermanGermany,
];

const getLinkText = (url: string) => {
  return url.split('/')[2];
};

const getUrlKey = (url: string, locale: Locale) => {
  return `${getLinkText(url)}-${locale}`;
};

export default function Home() {
  const [apiResponses, setApiResponses] = useState<{ [urlKey: string]: any }>({});
  const [loading, setLoading] = useState<{ [urlKey: string]: boolean }>({});
  const [error, setError] = useState<{ [urlKey: string]: any }>({});
  const { data: basicsPackagesMap } = useBasicsPackagesMap();

  const fetchApiData = async (url: string, locale: Locale) => {
    const urlWithLocale = url.replace('[locale]', locale);
    const urlWithBundleSlug = urlWithLocale.replace(
      '[bundleSlug]',
      // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'Locale' can't be used to index type '{ "en-US": { bike: string; "refurbished-bike": string; "bike-plus": string; "refurbished-bike-plus": string; tread: string; guide: string; row: string; "tread-plus": string; }; "en-AU": { bike: string; "bike-plus": string; tread: string; guide: string; }; "en-GB": { ...; }; "en-CA": { ...; }; "de-DE": { ...; }; }'.
      basicsPackagesMap?.bike,
    );

    const urlKey = getUrlKey(url, locale);
    if (loading[urlKey]) return;

    setLoading(prev => ({ ...prev, [urlKey]: true }));
    try {
      const response = await fetch(urlWithBundleSlug);
      const data = await response.json();
      setApiResponses(prev => ({ ...prev, [urlKey]: data }));
    } catch (err) {
      setError(prev => ({ ...prev, [urlKey]: err }));
      console.error(`Error fetching ${urlKey}:`, err);
    } finally {
      setLoading(prev => ({ ...prev, [urlKey]: false }));
    }
  };

  useEffect(() => {
    locales.forEach(locale => {
      Object.values(TestRoutes).forEach(rawUrl => {
        fetchApiData(rawUrl, locale);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: '0 20px' }}>
      <h2>API Statuses</h2>

      <table>
        <thead>
          <tr>
            <th>URL</th>
            {locales.map(locale => (
              <th key={locale}>{locale}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.values(TestRoutes).map(rawUrl => {
            const url = rawUrl.replace('[locale]', Locale.Default);
            return (
              <tr key={getLinkText(url)}>
                <td>{getLinkText(url)}</td>
                {locales.map((locale, idx) => {
                  const urlWithLocale = rawUrl.replace('[locale]', locale);
                  const urlWithBundleSlug = urlWithLocale.replace(
                    '[bundleSlug]',
                    // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'Locale' can't be used to index type '{ "en-US": { bike: string; "refurbished-bike": string; "bike-plus": string; "refurbished-bike-plus": string; tread: string; guide: string; row: string; "tread-plus": string; }; "en-AU": { bike: string; "bike-plus": string; tread: string; guide: string; }; "en-GB": { ...; }; "en-CA": { ...; }; "de-DE": { ...; }; }'.
                    basicsPackagesMap?.bike,
                  );
                  return (
                    <td
                      key={`${urlWithBundleSlug}-${idx}`}
                      style={{ textAlign: 'center' }}
                    >
                      <Link href={urlWithBundleSlug}>
                        {loading[getUrlKey(url, locale)] ? (
                          <LoadingBox />
                        ) : error[getUrlKey(url, locale)] ? (
                          <FailBox />
                        ) : apiResponses[getUrlKey(url, locale)] ? (
                          <SuccessBox />
                        ) : (
                          <LoadingBox />
                        )}
                      </Link>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SuccessBox = () => (
  <div
    style={{
      backgroundColor: 'green',
      padding: '2px',
      borderRadius: '3px',
      width: '10px',
      height: '10px',
      display: 'inline-block',
      marginRight: '5px',
    }}
  />
);

const FailBox = () => (
  <div
    style={{
      backgroundColor: 'red',
      padding: '2px',
      borderRadius: '3px',
      width: '10px',
      height: '10px',
      display: 'inline-block',
      marginRight: '5px',
    }}
  />
);

const LoadingBox = () => (
  <div
    style={{
      backgroundColor: '#f5a623',
      padding: '2px',
      borderRadius: '3px',
      width: '10px',
      height: '10px',
      position: 'relative',
      overflow: 'hidden',
      display: 'inline-block',
      marginRight: '5px',
    }}
  >
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '2px',
        top: '50%',
        left: 0,
        marginTop: '-1px',
        background: 'rgba(255, 255, 255, 0.7)',
        animation: 'loadingAnimation 1.5s infinite ease-in-out',
      }}
    />
    <style jsx>{`
      @keyframes loadingAnimation {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
    `}</style>
  </div>
);
